/**
 * Ask the page that frames this app to open *its* support widget (#5622).
 *
 * The job form runs in an iframe on the BSDIR page, which already has the floating
 * widget mounted. Opening that one instead of a second sheet in here keeps a single
 * Salesforce bootstrap, a single set of session flags and a single dismissal state on
 * the screen — and lets the sheet size itself to the viewport rather than to the
 * iframe's rectangle.
 *
 * The page answers either way, so the caller can fall back to its own sheet. It gets
 * `false` for every surface that has no such page: an app that is not framed, a Django
 * bundle that predates the listener (silence, answered by the timeout), and a widget
 * that declined to open — a persisted dismissal, a payload its core could not use.
 *
 * The message names and the timeout live in the design-system (`messages.ts`) with the
 * rest of the cross-window protocol: both halves have to spell them identically and a
 * mismatch is silent on either side. The other half lives in douleutaras'
 * `static/douleutaras/typescript/loaders/lazy-support-widget-loader.ts`.
 */
import {
  CROSS_FRAME_ANSWER_TIMEOUT_MS,
  SUPPORT_OPENED_MESSAGE,
  SUPPORT_OPEN_REQUEST_MESSAGE,
  SUPPORT_UNAVAILABLE_MESSAGE
} from 'Design-system/app/static/scripts/support-widget';

/**
 * Whether there is a page to ask at all. Callers check this first so an unframed
 * surface — ProList, the webview — never waits on an answer that cannot come, and
 * opens its own sheet on the click itself.
 */
export const canAskPageForSupportSheet = (): boolean =>
  window.parent !== window && Boolean(window.origin || window.location.origin);

export const requestPageSupportSheet = (): Promise<boolean> => {
  const origin = window.origin || window.location.origin;

  if (!canAskPageForSupportSheet()) {
    return Promise.resolve(false);
  }

  return new Promise<boolean>(resolve => {
    let timer: ReturnType<typeof setTimeout>;
    let settled = false;

    const finish = (opened: boolean) => {
      if (settled) { return; }

      settled = true;
      clearTimeout(timer);
      window.removeEventListener('message', handleAnswer);
      resolve(opened);
    };

    const handleAnswer = (event: MessageEvent) => {
      if (event.origin !== origin) { return; }

      if (event.data === SUPPORT_OPENED_MESSAGE) {
        finish(true);
      } else if (event.data === SUPPORT_UNAVAILABLE_MESSAGE) {
        finish(false);
      }
    };

    window.addEventListener('message', handleAnswer);
    timer = setTimeout(() => finish(false), CROSS_FRAME_ANSWER_TIMEOUT_MS);
    window.parent.postMessage(SUPPORT_OPEN_REQUEST_MESSAGE, origin);
  });
};

export default requestPageSupportSheet;
